import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class QuickAddBlockComponent extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    const variantId = this.#variantIdInput?.value;
    if (variantId) {
      this.#syncVariantState(variantId);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  get #variantMap() {
    const variantDataElement = this.querySelector('[data-quick-add-variant-data]');
    if (!variantDataElement?.textContent) return {};

    try {
      return JSON.parse(variantDataElement.textContent).reduce((accumulator, item) => {
        accumulator[String(item.id)] = item;
        return accumulator;
      }, {});
    } catch {
      return {};
    }
  }

  get #addToCartButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="addToCartButton"]'));
  }

  get #productLink() {
    return /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-quick-add-product-link]'));
  }

  get #sellingPlanInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="selling_plan"]'));
  }

  get #variantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
  }

  #syncVariantState(variantId) {
    const variantState = this.#variantMap[String(variantId)];
    if (!variantState) return;

    if (this.#productLink && variantState.url) {
      this.#productLink.href = variantState.url;
    }

    if (this.#sellingPlanInput) {
      this.#sellingPlanInput.value = variantState.sellingPlanId || '';
    }

    if (!this.#addToCartButton) return;

    const purchaseMode = this.dataset.purchaseMode;
    const canAddToCart = Boolean(variantState.available) && (purchaseMode !== 'subscription' || Boolean(variantState.sellingPlanId));

    if (canAddToCart) {
      this.#addToCartButton.removeAttribute('disabled');
    } else {
      this.#addToCartButton.setAttribute('disabled', '');
    }
  }

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #handleProductSelect = async (event) => {
    const { detail } = await event.promise;
    if (detail?.productId && String(detail.productId) !== this.dataset.productId) return;
    if (!detail?.resource?.id) return;

    this.#syncVariantState(detail.resource.id);
  };
}

if (!customElements.get('quick-add-block-component')) {
  customElements.define('quick-add-block-component', QuickAddBlockComponent);
}